/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.11.0
 */

import type { ChangeEvent, FC, ReactElement, RefObject } from 'react';
import { cloneElement, useCallback, useEffect, useRef, useState } from 'react';

import { KeyboardKeyEnum } from '@modules/app/appConstants';
import { usePreventWindowScroll } from '@modules/app/hooks/usePreventWindowScroll';

import type { IInputProps } from '../Input/Input';
import { FoundItemMemo } from './FoundItem';
import { FoundItemsVirtualListMemo } from './FoundItemsVirtualList';
import {
  InputSearchFoundItemsStyled,
  InputSearchStyled,
} from './styled/InputSearchStyled';
import type { IInputSearchItem, TInputSearchVirtual } from './types';

export interface IInputSearchProps {
  children: ReactElement<IInputProps>;
  foundItems?: IInputSearchItem[];
  virtual?: TInputSearchVirtual;

  onChange: (value: string) => void;
  onSelect: (item: IInputSearchItem) => void;
}

export const InputSearch: FC<IInputSearchProps> = ({
  children,
  foundItems,
  virtual,
  onChange,
  onSelect,
}) => {
  const listBodyRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  usePreventWindowScroll(listBodyRef);

  // main container ref
  const inputSearchItemsRef = useRef<HTMLDivElement>(null);

  const [isOpen, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setOpen(!!foundItems && foundItems.length > 0);
  }, [foundItems]);

  /*
   * -------------------------------------------
   *            Input handlers
   * -------------------------------------------
   */

  const onInputChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target) {
        const value = event.target.value || '';
        onChange(value);
      }
    },
    [onChange],
  );

  /*
   * -------------------------------------------
   *            Global events handlers
   * -------------------------------------------
   */

  const handleClickOutside = useCallback(
    (event: MouseEvent): void => {
      if (
        inputSearchItemsRef.current &&
        !inputSearchItemsRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        setOpen(false);
      }
    },
    [inputSearchItemsRef, isOpen],
  );

  const windowKeyDownHandler = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === KeyboardKeyEnum.ESCAPE && isOpen) {
        setOpen(false);
      }
    },
    [isOpen],
  );

  // TODO - wtf???

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutside, {
      once: true,
      capture: true,
    });
    window.addEventListener('keydown', windowKeyDownHandler, {
      once: true,
      capture: true,
    });
    return () => {
      removeEventListener('mousedown', handleClickOutside, { capture: true });
      removeEventListener('keydown', windowKeyDownHandler, { capture: true });
    };
  }, [handleClickOutside, windowKeyDownHandler]);

  const foundItemsComponents = foundItems?.map((item) => (
    <FoundItemMemo
      item={item}
      key={`${item.value}${item.id}`}
      setOpen={setOpen}
      onSelect={onSelect}
    />
  ));

  return (
    <InputSearchStyled>
      {cloneElement(children, {
        onChange: onInputChangeHandler,
        onClick: () => setOpen(!isOpen),
      })}
      {isOpen && !!foundItems && !!foundItems.length && (
        <InputSearchFoundItemsStyled ref={inputSearchItemsRef}>
          {virtual ? (
            <FoundItemsVirtualListMemo
              foundItems={foundItems}
              setOpen={setOpen}
              virtual={virtual}
              onSelect={onSelect}
            />
          ) : (
            foundItemsComponents
          )}
        </InputSearchFoundItemsStyled>
      )}
    </InputSearchStyled>
  );
};
